<script setup>
import { computed, reactive, ref, watch } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  company: {
    type: Object,
    default: null,
  },
  onCreate: {
    type: Function,
    default: async () => {},
  },
});

defineEmits(['close']);

const form = reactive({
  name: '',
  email: '',
  phoneNumber: '',
  dialCode: '+20',
});
const error = ref('');
const isSaving = ref(false);

const companyName = computed(() => props.company?.name || '');

watch(
  () => props.show,
  show => {
    if (show) {
      form.name = '';
      form.email = '';
      form.phoneNumber = '';
      form.dialCode = '+20';
      error.value = '';
      isSaving.value = false;
    }
  }
);

const normalizedPhone = computed(() => {
  const value = form.phoneNumber.trim();
  if (!value) return '';
  if (value.startsWith('+')) return value;
  return `${form.dialCode}${value.replace(/^0+/, '')}`;
});

const submit = async () => {
  if (!form.name.trim()) {
    error.value = 'Add a contact name.';
    return;
  }

  isSaving.value = true;
  error.value = '';
  try {
    await props.onCreate({
      name: form.name.trim(),
      email: form.email.trim(),
      phone_number: normalizedPhone.value,
      additional_attributes: {
        company_name: companyName.value,
        tacc_business_id: props.company?.id || '',
        tacc_business_odoo_id: props.company?.odoo_id || props.company?.odooId || '',
      },
    });
  } catch (err) {
    error.value = err?.message || 'Could not create contact.';
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1000] flex items-center justify-center bg-n-alpha-3 px-4"
    @click.self="$emit('close')"
  >
    <section
      class="w-full max-w-[520px] overflow-hidden rounded-xl bg-n-solid-1 shadow-xl"
      role="dialog"
      aria-modal="true"
    >
      <header class="flex items-start gap-4 px-6 py-5">
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-blue-10"
        >
          <span class="i-lucide-users-round size-5" />
        </div>
        <div class="min-w-0 flex-1">
          <h2 class="m-0 text-lg font-semibold text-n-slate-12">
            Add related contact
          </h2>
          <p class="m-0 mt-1 text-sm text-n-slate-10">
            Create a contact under {{ companyName }}.
          </p>
        </div>
        <button
          type="button"
          class="flex size-7 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2"
          @click="$emit('close')"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </header>
      <div class="h-px bg-n-weak" />
      <form id="tacc-related-contact-form" class="grid gap-4 px-6 py-5" @submit.prevent="submit">
        <label class="grid gap-1.5">
          <span class="text-sm font-medium text-n-slate-11">Name</span>
          <input v-model="form.name" class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Contact name" />
        </label>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label class="grid gap-1.5">
            <span class="text-sm font-medium text-n-slate-11">Email</span>
            <input v-model="form.email" type="email" class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="name@company.com" />
          </label>
          <label class="grid gap-1.5">
            <span class="text-sm font-medium text-n-slate-11">Phone</span>
            <div class="flex h-10 overflow-hidden rounded-lg border border-n-weak bg-n-alpha-1">
              <select v-model="form.dialCode" class="w-16 border-0 bg-transparent px-2 text-sm outline-none">
                <option value="+20">🇪🇬</option>
                <option value="+971">🇦🇪</option>
                <option value="+966">🇸🇦</option>
                <option value="+1">🇺🇸</option>
              </select>
              <input v-model="form.phoneNumber" class="min-w-0 flex-1 border-0 bg-transparent px-3 text-sm outline-none" inputmode="tel" placeholder="100 000 0000" />
            </div>
          </label>
        </div>
        <label class="grid gap-1.5">
          <span class="text-sm font-medium text-n-slate-11">Company</span>
          <input :value="companyName" class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-10" readonly />
        </label>
        <p v-if="error" class="m-0 text-xs text-n-ruby-10">{{ error }}</p>
      </form>
      <div class="h-px bg-n-weak" />
      <footer class="flex justify-end gap-2 px-6 py-4">
        <NextButton faded slate label="Cancel" @click="$emit('close')" />
        <NextButton
          blue
          form="tacc-related-contact-form"
          type="submit"
          label="Create contact"
          :is-loading="isSaving"
        />
      </footer>
    </section>
  </div>
</template>
